import { useEffect, useRef } from "react";

/**
 * SingleTabGuard — keep Chairside running in only ONE tab per browser (per origin).
 *
 * "Newest tab wins": when a new tab opens it CLAIMS the app; any previously-open
 * tab receives the claim and LEAVES the app (no UI) — it tries window.close() and
 * otherwise navigates to about:blank. So you never end up with two live tabs.
 *
 * No UI is rendered — children always render; a superseded tab simply redirects.
 *
 * Transport: BroadcastChannel (instant) with a localStorage fallback. Both are
 * same-origin, so http://localhost:5173 and https://chairside.digitalpresencematters.com
 * are isolated automatically — same code works on both, on every route.
 *
 * IMPORTANT: the superseded tab must go somewhere OUTSIDE the app (about:blank).
 * Redirecting it back into the app would make it re-claim and ping-pong forever.
 */
const CHANNEL = "chairside-single-tab";
const REDIRECT_URL = "about:blank"; // where a superseded tab goes (no app UI, no loop)

export default function SingleTabGuard({ children }) {
  const idRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const tsRef = useRef(0);
  const leftRef = useRef(false);

  useEffect(() => {
    const id = idRef.current;

    const leave = () => {
      if (leftRef.current) return;
      leftRef.current = true;
      // window.close() only works for script-opened tabs; navigating away is the
      // reliable fallback for normal user-opened tabs.
      try {
        window.close();
      } catch {
        /* ignore */
      }
      try {
        window.location.replace(REDIRECT_URL);
      } catch {
        /* ignore */
      }
    };

    // ---- Primary: BroadcastChannel ----
    if (typeof BroadcastChannel !== "undefined") {
      const ch = new BroadcastChannel(CHANNEL);
      const claim = () => {
        tsRef.current = Date.now();
        try {
          ch.postMessage({ id, ts: tsRef.current });
        } catch {
          /* ignore */
        }
      };
      ch.onmessage = (e) => {
        const m = e?.data;
        if (!m || m.id === id) return;
        // A newer tab claimed -> this (older) tab is the "previous" one -> leave.
        const newer =
          m.ts > tsRef.current ||
          (m.ts === tsRef.current && String(m.id) > String(id));
        if (newer) {
          try {
            ch.close();
          } catch {
            /* ignore */
          }
          leave();
        }
      };
      claim();
      return () => {
        ch.onmessage = null;
        try {
          ch.close();
        } catch {
          /* ignore */
        }
      };
    }

    // ---- Fallback: localStorage claim + 'storage' event ----
    const onStorage = (e) => {
      if (e.key !== CHANNEL || !e.newValue) return;
      try {
        const v = JSON.parse(e.newValue);
        if (v && v.id !== id) leave(); // another tab claimed -> this one leaves
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", onStorage);
    try {
      localStorage.setItem(CHANNEL, JSON.stringify({ id, ts: Date.now() }));
    } catch {
      /* storage unavailable -> no enforcement */
    }
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return children;
}
